/**
 * SR Size Gate v2 (Dan, 2026-07-13; revised 2026-08-20)
 *
 * Business rules:
 *  - On page load, no size is selected.
 *  - Add-to-cart (and Shop Pay / accelerated checkout) is held until a size
 *    is picked; a blocked attempt shows "Please select your size first."
 *  - Switching color PRESERVES the chosen size (revised 2026-08-20 — the
 *    original v1 spec deselected it). If no size was chosen yet, none stays
 *    selected after a color switch.
 *
 * v2 exists because two scripts undo v1's work after load:
 *  1. An app embed (cdn.shopify.com/extensions/01a01daf-…) programmatically
 *     clicks the default size radio ~1s after load. v1 read the resulting
 *     change event as a deliberate user pick and then protected the wrong
 *     selection. isTrusted can't filter it — Chromium fires the change
 *     event of a programmatic radio .click() with isTrusted=true — so a
 *     size change only counts as deliberate when a trusted pointer/keyboard
 *     interaction touched that size fieldset in the preceding moment.
 *  2. The theme morphs the picker in place after every option change
 *     (morph.js copyAttributes + variant-picker.js #restoreUserSelection),
 *     re-checking its remembered size without adding DOM nodes.
 * v2 therefore holds the user's intent per picker and continuously
 * re-enforces it: an attribute-level MutationObserver, timed passes after
 * every option change, and a slow safety interval. All enforcement is
 * idempotent and only writes when state actually differs, so the passes
 * are cheap and cannot loop against each other or the theme.
 *
 * Scope: any variant-picker with a Size option, EXCEPT pickers inside
 * collection-grid product cards (swatch-only, no purchase button in scope).
 * Products without a Size option are untouched.
 */
(function () {
  'use strict';

  var MSG_TEXT = 'Please select your size first.';
  // picker element -> the size value the user deliberately tapped (string),
  // or absent/null meaning "no deliberate choice yet".
  var desired = new WeakMap();

  var css =
    // The theme's sliding "selected" pill assumes one option is always checked;
    // with none checked it sits at offset 0 on EVERY label (solid filled boxes,
    // unreadable labels). Hide pills in any fieldset with no checked input.
    'fieldset.variant-option--buttons:not(:has(input:checked)) .variant-option__button-label__pill{opacity:0;}' +
    '.sr-gate-msg{display:none;margin-top:8px;padding:10px 12px;border:1.5px solid #b3261e;' +
    'border-radius:6px;background:#fdf2f2;color:#b3261e;font-size:14px;font-weight:600;line-height:1.3;}' +
    '.sr-gate-msg.sr-gate-msg--visible{display:block;}' +
    '.sr-gate-hold{position:relative;}' +
    '.sr-gate-hold::after{content:"";position:absolute;inset:0;z-index:5;cursor:not-allowed;background:transparent;}' +
    '.sr-gate-hold > *{opacity:.5;}' +
    '.sr-gate-pulse{outline:2px solid #b3261e;outline-offset:4px;border-radius:4px;' +
    'transition:outline-color .3s ease;}' +
    '@media (prefers-reduced-motion: no-preference){.sr-gate-pulse{animation:sr-gate-pulse 1.2s ease 1;}}' +
    '@keyframes sr-gate-pulse{0%,100%{outline-color:#b3261e;}50%{outline-color:rgba(179,38,30,.15);}}' +
    // While held, never paint the success animation the theme may still trigger.
    'add-to-cart-component button[aria-disabled="true"]{transition:none!important;}' +
    'add-to-cart-component button[aria-disabled="true"][data-added="true"] .add-to-cart__added{display:none!important;}' +
    'add-to-cart-component button[aria-disabled="true"][data-added="true"] .add-to-cart-text{clip-path:none!important;opacity:1!important;filter:none!important;translate:0 0!important;}';

  function injectCss() {
    if (document.getElementById('sr-size-gate-css')) return;
    var style = document.createElement('style');
    style.id = 'sr-size-gate-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /** The Size fieldset of a picker, or null. Matches on the input's name
   *  attribute ("Size-<block>-<product>") — legend text also contains the
   *  selected value and the Find My Size button, so it isn't reliable. */
  function sizeFieldset(picker) {
    var input = picker.querySelector('fieldset input[type="radio"][name^="Size-"]');
    return input ? input.closest('fieldset') : null;
  }

  function isGated(picker) {
    if (picker.closest('product-card')) return null; // collection-grid swatches: leave alone
    return sizeFieldset(picker);
  }

  /** The DOM neighborhood that holds this picker's buy buttons. */
  function scopeOf(picker) {
    return (
      picker.closest('quick-add-dialog') ||
      picker.closest('dialog') ||
      picker.closest('featured-product-information') ||
      picker.closest('.shopify-section') ||
      document
    );
  }

  function paymentContainers(scope) {
    return scope.querySelectorAll(
      '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [data-shopify-buttoncontainer]'
    );
  }

  function atcButtons(scope) {
    return scope.querySelectorAll(
      'add-to-cart-component button, form[data-type="add-to-cart-form"] button[type="submit"], button[name="add"]'
    );
  }

  function msgFor(scope, create) {
    var msg = scope.querySelector('.sr-gate-msg');
    if (!msg && create) {
      var anchor = null;
      var buttons = atcButtons(scope);
      if (buttons.length) anchor = buttons[0].closest('add-to-cart-component') || buttons[0];
      if (anchor && anchor.parentNode) {
        msg = document.createElement('div');
        msg.className = 'sr-gate-msg';
        msg.setAttribute('role', 'alert');
        msg.textContent = MSG_TEXT;
        anchor.parentNode.insertBefore(msg, anchor.nextSibling);
      }
    }
    return msg;
  }

  /** Visually deselect every size radio. Idempotent: writes only when a
   *  radio is actually checked / marked, so re-running it is free. */
  function deselectSize(fieldset) {
    var changed = false;
    fieldset.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      if (radio.checked) {
        radio.checked = false;
        changed = true;
      }
      if (radio.hasAttribute('checked')) radio.removeAttribute('checked');
      if (radio.dataset.currentChecked === 'true') radio.dataset.currentChecked = 'false';
      if (radio.dataset.previousChecked === 'true') radio.dataset.previousChecked = 'false';
    });
    var legendValue = fieldset.querySelector('legend .variant-option__swatch-value');
    if (legendValue && legendValue.textContent !== '') legendValue.textContent = '';
    fieldset.style.removeProperty('--pill-width-current');
    fieldset.style.removeProperty('--pill-width-previous');
    return changed;
  }

  /** Re-select the user's chosen size after the theme re-rendered the picker
   *  back to its server default. Fires a real change event when it flips the
   *  radio, so the theme updates its own selected-variant state (price, URL,
   *  the variant id the cart form submits) exactly as if the user tapped it. */
  function restoreSize(fieldset, value) {
    var radios = fieldset.querySelectorAll('input[type="radio"]');
    var target = null;
    radios.forEach(function (radio) {
      if (radio.value === value) target = radio;
    });
    if (!target) return false; // chosen size doesn't exist on this color
    if (target.checked) return true; // already right; nothing to do
    radios.forEach(function (radio) {
      if (radio !== target && radio.checked) radio.checked = false;
    });
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function syncBuyUi(picker) {
    var scope = scopeOf(picker);
    var held = !desired.get(picker);
    paymentContainers(scope).forEach(function (el) {
      if (el.classList.contains('sr-gate-hold') !== held) el.classList.toggle('sr-gate-hold', held);
    });
    atcButtons(scope).forEach(function (btn) {
      if (held) {
        if (btn.getAttribute('aria-disabled') !== 'true') btn.setAttribute('aria-disabled', 'true');
      } else if (btn.hasAttribute('aria-disabled')) {
        btn.removeAttribute('aria-disabled');
      }
    });
    if (!held) {
      var msg = msgFor(scope, false);
      if (msg) msg.classList.remove('sr-gate-msg--visible');
      var fs = sizeFieldset(picker);
      if (fs) fs.classList.remove('sr-gate-pulse');
    }
  }

  /** Make one picker match the user's intent. Idempotent. */
  function enforce(picker) {
    var fieldset = isGated(picker);
    if (!fieldset) return;
    var want = desired.get(picker);
    if (!want) {
      deselectSize(fieldset);
    } else if (!restoreSize(fieldset, want)) {
      // Chosen size vanished (color without that size) -> back to "not chosen".
      desired.delete(picker);
      deselectSize(fieldset);
    }
    syncBuyUi(picker);
  }

  function enforceAll(root) {
    (root || document).querySelectorAll('variant-picker').forEach(enforce);
  }

  // Debounced scheduling so observer bursts collapse into one pass.
  var scheduled = false;
  function scheduleEnforce() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      enforceAll();
    });
  }

  /** The theme fetches new section HTML after an option change and morphs it
   *  in some time later. Run extra passes across that window so the reset
   *  gets corrected as soon as it lands, whatever the network latency. */
  function enforceSoonAndLater() {
    scheduleEnforce();
    [150, 400, 800, 1600, 3000].forEach(function (ms) {
      setTimeout(scheduleEnforce, ms);
    });
  }

  function block(event, picker) {
    event.preventDefault();
    event.stopImmediatePropagation();
    var scope = scopeOf(picker);
    var buttons = atcButtons(scope);
    var strip = function () {
      buttons.forEach(function (b) { b.removeAttribute('data-added'); });
    };
    strip();
    requestAnimationFrame(strip); // runs after the theme's own click handler, before paint
    [60, 250, 600, 1100].forEach(function (ms) { setTimeout(strip, ms); });
    var msg = msgFor(scope, true);
    if (msg) msg.classList.add('sr-gate-msg--visible');
    var fieldset = sizeFieldset(picker);
    if (fieldset) {
      fieldset.classList.remove('sr-gate-pulse');
      // restart the pulse animation
      void fieldset.offsetWidth;
      fieldset.classList.add('sr-gate-pulse');
      fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /** The gated picker responsible for a purchase attempt, or null. */
  function gatedPickerFor(element) {
    var scope =
      element.closest('quick-add-dialog') ||
      element.closest('dialog') ||
      element.closest('featured-product-information') ||
      element.closest('.shopify-section');
    if (!scope) return null;
    var pickers = scope.querySelectorAll('variant-picker');
    for (var i = 0; i < pickers.length; i++) {
      if (isGated(pickers[i]) && !desired.get(pickers[i])) return pickers[i];
    }
    return null;
  }

  // --- tell real shoppers apart from scripts ---------------------------------
  // A human pick always starts with a trusted pointer / touch / keyboard
  // event on the size fieldset. A script's radio.click() produces none of
  // those (its click event is untrusted; only its follow-up change event
  // masquerades as trusted). Record the touch, honor the change briefly.
  var lastTouch = { fieldset: null, t: 0 };
  function noteTouch(event) {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    var picker = event.target.closest('variant-picker');
    if (!picker) return;
    var fieldset = isGated(picker);
    if (fieldset && fieldset.contains(event.target)) {
      lastTouch.fieldset = fieldset;
      lastTouch.t = performance.now();
    }
  }
  ['pointerdown', 'mousedown', 'touchstart', 'keydown'].forEach(function (type) {
    window.addEventListener(type, noteTouch, true);
  });

  // --- user changes an option ------------------------------------------------
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      var picker = input.closest('variant-picker');
      if (!picker) return;
      var fieldset = isGated(picker);
      if (!fieldset) return;
      if (fieldset.contains(input)) {
        var deliberate =
          lastTouch.fieldset === fieldset && performance.now() - lastTouch.t < 1500;
        if (deliberate && input.checked) desired.set(picker, input.value);
        syncBuyUi(picker);
      }
      // Any option change (size or color) is followed by a theme re-render
      // that restores the server-default size — chase it down.
      enforceSoonAndLater();
    },
    true
  );

  // --- theme signals a picker update (kept from v1; harmless if it never fires)
  document.addEventListener('variant:update', function () {
    scheduleEnforce();
  });

  // --- purchase attempts ------------------------------------------------------
  // Bound on WINDOW (capture) so we run BEFORE the theme's document-level
  // delegated handlers (component.js registers first; same-phase order is
  // registration order, but window-capture always precedes document).
  window.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
      var picker = gatedPickerFor(form);
      if (picker) block(event, picker);
    },
    true
  );

  window.addEventListener(
    'click',
    function (event) {
      if (!(event.target instanceof Element)) return;
      var btn = event.target.closest(
        'add-to-cart-component button, form[data-type="add-to-cart-form"] button[type="submit"], button[name="add"]'
      );
      if (!btn) return;
      var picker = gatedPickerFor(btn);
      if (picker) block(event, picker);
    },
    true
  );

  // --- boot -------------------------------------------------------------------
  function boot() {
    injectCss();
    enforceAll();
    // The theme hydrates/re-renders shortly after load and re-checks the
    // server-default size; sweep the first seconds so it never sticks.
    [150, 400, 800, 1600, 3000, 5000].forEach(function (ms) {
      setTimeout(scheduleEnforce, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Late-rendered pickers (quick-add modals, section loads in the editor).
  document.addEventListener('shopify:section:load', function () {
    scheduleEnforce();
  });

  // The theme morphs the picker IN PLACE (attribute writes, no added nodes),
  // so watch attributes as well as child-list changes. Enforcement only
  // writes when state differs, and our own writes are property-level where
  // possible, so this cannot feed back into itself.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      var node = m.target;
      if (!(node instanceof Element)) continue;
      if (m.type === 'childList') {
        var added = m.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var a = added[j];
          if (!(a instanceof Element)) continue;
          if (a.tagName === 'VARIANT-PICKER' || (a.querySelector && a.querySelector('variant-picker'))) {
            scheduleEnforce();
            return;
          }
        }
      } else if (node.closest && (node.closest('variant-picker') || node.closest('add-to-cart-component'))) {
        scheduleEnforce();
        return;
      }
    }
  }).observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked', 'data-current-checked', 'aria-disabled', 'data-added'],
  });

  // Slow safety net: whatever re-render path the theme takes that none of the
  // hooks above catch, this corrects it within a second. Idempotent, cheap.
  setInterval(scheduleEnforce, 1000);
})();
